import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    PlanFeatureMessage,
    PricingPeriod,
    CloudProductKey,
    JFIND_1384_PRICE_FINDER_CONTAINER
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import { CtaButton } from "@/components/wpl/pricing/src/price-finder/price-card/cta-button.tsx";
import { Tooltip } from "@/libraries/design/components/tooltip/src/tooltip";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

import {
    PriceSlash,
    FreeTag14Days,
    FreeTag30Days,
    RecommendationBanner
} from "./components/adornments.tsx";
import { FeaturesSection } from "./components/features-section.tsx";

export interface Jfind1384PriceCardProps {
    first?: boolean;
    last?: boolean;
    highlighted?: boolean;
    isRecommendedPlan: boolean;
    planName: string;
    description?: string;
    adjacentLowerTierPlanName?: string;
    planFeatureMessages?: PlanFeatureMessage[];
    ctaText: string;
    ctaUrl: string;
    ctaId: string;
    isCtaDisabled?: boolean;
    billingCycle: PricingPeriod;
    priceCalcIdx: number;
    contactURL?: string;
    offeringSku?: string;
    enableRecommendation?: boolean;
    shouldAddDiscount?: boolean;
    edition: string | undefined;
    dataProductKey: CloudProductKey;
    price?: string;
    priceDisplayMessage?: ReactNode;
    priceSlash?: "purple" | "yellow";
    freeTag?: "14days" | "30days";
    numberFormatLocale?: "en-US" | "ja-JP";
    unitCount: number;
    unitTierLowerBound?: string | undefined;
    unitTierUpperBound?: string | undefined;
}

// eslint-disable-next-line complexity
export const Jfind1384PriceCard = ({
    first,
    last,
    highlighted = false,
    isRecommendedPlan,
    planName,
    description,
    adjacentLowerTierPlanName,
    planFeatureMessages = [],
    ctaText,
    ctaId,
    ctaUrl,
    isCtaDisabled = false,
    billingCycle,
    dataProductKey,
    edition,
    price,
    priceDisplayMessage,
    priceSlash,
    freeTag,
    numberFormatLocale = "en-US",
    unitCount = 0,
    unitTierLowerBound,
    unitTierUpperBound
}: Jfind1384PriceCardProps) => {
    const intl = useIntl();
    const perUserPerMonthText = intl.formatMessage({
        id: "JiraSoftwarePricingPageV2.priceFinder.cardMonthlyPerUnitCount",
        defaultMessage: "per user / month"
    });
    const perYearText = intl.formatMessage({
        id: "Pricing.PriceCard.Annual.Description",
        defaultMessage: "per year"
    });
    const unitTierText = {
        id: "JiraSoftwarePricingPage.priceFinder.cardUnitCountTierRange",
        defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
    };
    const tooltipAriaLabel = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.MoreInformationAboutTier",
        defaultMessage: "More information about tier"
    });
    const filledSeatsMessage = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.Details",
        defaultMessage: "Filled seats"
    });
    const availableSeatsMessage = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.AvailableSeats",
        defaultMessage: "Available seats"
    });

    const formatNumber = new Intl.NumberFormat(numberFormatLocale).format;
    const formattedUnitTierNode = unitTierLowerBound && unitTierUpperBound && (
        <>
            <FormattedMessage
                {...unitTierText}
                values={{
                    ...DEFAULT_VALUES,
                    lowerLimit: formatNumber(unitTierLowerBound),
                    upperLimit: formatNumber(unitTierUpperBound)
                }}
            />
            <Tooltip
                ariaLabel={tooltipAriaLabel}
                buttonSize="small"
                content={`Tier:
                        ${formatNumber(
                            unitTierLowerBound
                        )} -${" "}${formatNumber(unitTierUpperBound)}

                        ${filledSeatsMessage}:
                        ${formatNumber(unitCount)}

                        ${availableSeatsMessage}:
                        ${formatNumber(unitTierUpperBound - unitCount)}`}
            />
        </>
    );

    let freeTagElem: ReactNode = null;
    if (freeTag === "14days") {
        freeTagElem = <FreeTag14Days />;
    }
    if (freeTag === "30days") {
        freeTagElem = <FreeTag30Days />;
    }

    let highlightedFeaturesTitle = (
        <FormattedMessage
            id="Pricing.PriceCard.featureMessageTitleFree"
            defaultMessage="Includes:"
        />
    );
    if (adjacentLowerTierPlanName) {
        highlightedFeaturesTitle = (
            <FormattedMessage
                id="Pricing.PriceCard.planFeatureMessageTitle"
                defaultMessage="Everything from {featureMessagePlanName}, plus:"
                values={{
                    featureMessagePlanName: adjacentLowerTierPlanName
                }}
            />
        );
    }

    return (
        <div
            css={[
                cardStyles,
                css({
                    border: highlighted
                        ? `2px solid ${wacToken("color.primary.blue")}`
                        : `1px solid ${wacToken("color.neutral.light300")}`,
                    padding: !highlighted ? "1px" : 0,
                    zIndex: highlighted ? 1 : "auto"
                }),
                first && firstBorderRadiusStyles,
                last && lastBorderRadiusStyles,
                !first && !last && centerCardMarginStyles
            ]}
        >
            <div
                css={[
                    css({
                        height: "32px",
                        overflow: "hidden"
                    }),
                    first &&
                        css({
                            borderStartStartRadius: "18px",
                            [media.below.md]: {
                                borderStartEndRadius: "18px"
                            }
                        }),
                    last &&
                        css({
                            [media.above.md]: {
                                borderStartEndRadius: "18px"
                            }
                        })
                ]}
            >
                {isRecommendedPlan && <RecommendationBanner />}
            </div>
            <section css={topSectionStyles}>
                <section css={headerSectionStyles}>
                    {/* header section */}
                    <h3
                        css={css({
                            fontSize: "32px",
                            lineHeight: "38px",
                            margin: token("space.0")
                        })}
                    >
                        {planName}
                    </h3>
                    <div>{description}</div>
                </section>
                {/* price and cta section */}
                {price ? (
                    <div css={priceSectionStyles}>
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                flexWrap: "nowrap",
                                gap: token("space.200")
                            })}
                        >
                            <div
                                css={css({
                                    position: "relative",
                                    overflow: "visible"
                                })}
                            >
                                <span css={priceSpanStyles}>{price}</span>
                                {priceSlash && (
                                    <PriceSlash color={priceSlash} />
                                )}
                            </div>
                            {freeTagElem}
                        </div>
                        <div
                            css={css({
                                height: token("space.300")
                            })}
                        >
                            {billingCycle === "MONTHLY" &&
                                price != "$0" &&
                                perUserPerMonthText}
                            {billingCycle === "ANNUAL" &&
                                price != "$0" &&
                                perYearText}
                        </div>
                        <div
                            css={css({
                                height: token("space.300"),
                                display: "flex"
                            })}
                        >
                            {billingCycle === "ANNUAL" && formattedUnitTierNode}
                        </div>
                    </div>
                ) : (
                    <div css={priceSectionStyles}>
                        <div
                            css={css({
                                height: "73px", // Matches the height of price + "per user/month" text
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start"
                            })}
                        >
                            <span
                                css={css({
                                    fontSize: wacToken("font.size.400"),
                                    lineHeight: wacToken("font.lineHeight.400"),
                                    textAlign: "left"
                                })}
                            >
                                {priceDisplayMessage}
                            </span>
                        </div>
                        <div
                            css={css({
                                height: token("space.600")
                            })}
                        />
                    </div>
                )}
                <div
                    css={css({
                        "display": "flex",
                        "justifyContent": "center",
                        "alignItems": "center",
                        "gap": token("space.100"),
                        "width": "100%",
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "& > div": {
                            "width": "100%",
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            "& > button, & > a": {
                                "width": "100%",
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                "& > span": {
                                    width: "100%"
                                }
                            }
                        },
                        [media.below.md]: {
                            maxWidth: "284px"
                        }
                    })}
                >
                    <CtaButton
                        id={ctaId}
                        dataEventPropsLabel={`${planName}-get-it-free`}
                        dataProductKey={dataProductKey}
                        edition={edition}
                        url={ctaUrl}
                        type={isRecommendedPlan ? "primary" : "secondary"}
                        text={ctaText}
                        isDisabled={isCtaDisabled}
                        containerId={JFIND_1384_PRICE_FINDER_CONTAINER}
                        target="_self"
                    />
                </div>
            </section>
            {!!planFeatureMessages.length && (
                <div css={featuresSectionOverrideStyles}>
                    <FeaturesSection
                        planFeatureMessages={planFeatureMessages}
                        highlightedFeaturesTitle={highlightedFeaturesTitle}
                    />
                </div>
            )}
        </div>
    );
};

const fontCharlieDisplay =
    '"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
    'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const priceSpanStyles = css({
    fontSize: "48px",
    fontWeight: 500,
    fontFamily: fontCharlieDisplay,
    position: "relative"
});

const cardStyles = css({
    color: wacToken("color.primary.black"),
    overflow: "visible",
    gap: "52px",
    [media.above.md]: {
        flex: "0 1 364px"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const firstBorderRadiusStyles = css({
    borderStartStartRadius: wacToken("corner.radius.card"),
    [media.above.md]: {
        borderEndStartRadius: wacToken("corner.radius.card")
    },
    [media.below.md]: {
        borderStartEndRadius: wacToken("corner.radius.card")
    }
});
const lastBorderRadiusStyles = css({
    borderEndEndRadius: wacToken("corner.radius.card"),
    [media.above.md]: {
        borderStartEndRadius: wacToken("corner.radius.card")
    },
    [media.below.md]: {
        borderEndStartRadius: wacToken("corner.radius.card")
    }
});
const centerCardMarginStyles = css({
    [media.above.md]: {
        marginInlineStart: "-1px",
        marginInlineEnd: "-1px"
    },
    [media.below.md]: {
        marginBlockStart: "-1px",
        marginBlockEnd: "-1px"
    }
});

const topSectionStyles = css({
    paddingBlockStart: token("space.300"),
    paddingBlockEnd: token("space.200"),
    paddingInlineStart: token("space.500"),
    paddingInlineEnd: token("space.500")
});

const headerSectionStyles = css({
    marginBlockEnd: token("space.600"),
    height: "94px",
    maxHeight: "94px",
    gap: token("space.100")
});

const priceSectionStyles = css({
    marginBlockEnd: token("space.400"),
    position: "relative"
});

const featuresSectionOverrideStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > section": {
        paddingInline: 0,
        paddingBlockEnd: 0
    }
});
